/**
 * Shopping Cart Modal
 * Global shopping cart overlay for merch purchases with Stripe checkout
 * Note: Stripe is temporarily disabled, all payments go through Stripe
 */

import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { useState } from 'react';
import { 
  X, ShoppingCart, Trash2, Plus, Minus, Package, 
  CreditCard, Truck, AlertCircle, CheckCircle, Lock
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ShoppingCartModal() {
  const { cart, loading, isOpen, setIsOpen, updateQuantity, removeFromCart, clearCart, checkout } = useCart();
  const { isAuthenticated } = useAuth();
  const [showShipping, setShowShipping] = useState(false);
  const [shippingAddress, setShippingAddress] = useState({
    name: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    country: ''
  });
  const [checkingOut, setCheckingOut] = useState(false);

  if (!isOpen) return null;

  const handleQuantityChange = async (merchId, size, currentQty, delta) => {
    const newQty = currentQty + delta;
    if (newQty <= 0) {
      await removeFromCart(merchId, size);
    } else {
      await updateQuantity(merchId, newQty, size);
    }
  };

  const handleCheckout = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to checkout');
      return;
    }

    if (cart.items.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    // Validate shipping address if shown
    if (showShipping) {
      const requiredFields = ['name', 'street', 'city', 'state', 'zip', 'country'];
      const missingFields = requiredFields.filter(f => !shippingAddress[f]?.trim());
      if (missingFields.length > 0) {
        toast.error('Please fill in all shipping address fields');
        return;
      }
    }

    setCheckingOut(true);
    try {
      const result = await checkout(showShipping ? shippingAddress : null);
      if (result?.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = result.checkout_url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
    } finally {
      setCheckingOut(false);
    }
  };

  // Platform fee is 10%
  const platformFee = cart.total * 0.10;
  const grandTotal = cart.total;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={() => setIsOpen(false)}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-lg max-h-[90vh] bg-[#0a0a0a] border border-[#1a1a1a] shadow-2xl overflow-hidden flex flex-col mx-4">
        {/* Header */}
        <div className="p-4 border-b border-[#1a1a1a] flex items-center justify-between bg-[#111]">
          <div className="flex items-center gap-3">
            <ShoppingCart size={24} className="text-[#00CCFF]" />
            <h2 className="text-white font-['Chakra_Petch'] uppercase text-lg">
              Shopping Cart
            </h2>
            {cart.item_count > 0 && (
              <span className="px-2 py-0.5 bg-[#00CCFF] text-black text-xs font-mono rounded-full">
                {cart.item_count}
              </span>
            )}
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="text-[#888] hover:text-white transition-colors"
            data-testid="close-cart-modal"
          >
            <X size={24} />
          </button>
        </div>
        
        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {cart.items.length === 0 ? (
            <div className="text-center py-12">
              <Package size={48} className="mx-auto mb-4 text-[#333]" />
              <p className="text-[#888] font-mono">Your cart is empty</p>
              <p className="text-[#666] font-mono text-sm mt-2">
                Browse artist merch stores to add items
              </p>
            </div>
          ) : (
            <>
              {cart.items.map((item, index) => (
                <div 
                  key={`${item.merch_id}-${item.size || 'default'}-${index}`}
                  className="flex gap-4 p-3 bg-[#111] border border-[#1a1a1a] rounded"
                  data-testid={`cart-item-${item.merch_id}`}
                >
                  {/* Item Image */}
                  <div className="w-20 h-20 bg-[#1a1a1a] rounded overflow-hidden flex-shrink-0">
                    {item.image_url || item.image || item.image_filename ? (
                      <img
                        src={item.image_url || (item.image ? `${API}/merch/image/${item.image}` : `${API}/merch/image/${item.image_filename}`)}
                        alt={item.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package size={24} className="text-[#333]" />
                      </div>
                    )}
                  </div>
                  
                  {/* Item Details */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-['Chakra_Petch'] text-sm truncate">
                      {item.name}
                    </h3>
                    <p className="text-[#666] font-mono text-xs">
                      by {item.artist_username}
                    </p>
                    {item.size && (
                      <span className="inline-block mt-1 px-2 py-0.5 bg-[#1a1a1a] text-[#888] text-xs font-mono rounded">
                        Size: {item.size}
                      </span>
                    )}
                    
                    {/* Stock Warning */}
                    {!item.in_stock && (
                      <div className="flex items-center gap-1 mt-1 text-[#ff6b6b] text-xs font-mono">
                        <AlertCircle size={12} />
                        <span>Low stock</span>
                      </div>
                    )}
                    
                    {/* Price & Quantity */}
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-[#00ff88] font-['Chakra_Petch']">
                        ${item.item_total?.toFixed(2)}
                      </p>
                      
                      {/* Quantity Controls */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleQuantityChange(item.merch_id, item.size, item.quantity, -1)}
                          className="w-7 h-7 flex items-center justify-center bg-[#1a1a1a] hover:bg-[#222] text-white rounded"
                          disabled={loading}
                          data-testid={`decrease-qty-${item.merch_id}`}
                        >
                          <Minus size={14} />
                        </button>
                        <span className="text-white font-mono text-sm w-8 text-center">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => handleQuantityChange(item.merch_id, item.size, item.quantity, 1)}
                          className="w-7 h-7 flex items-center justify-center bg-[#1a1a1a] hover:bg-[#222] text-white rounded"
                          disabled={loading}
                          data-testid={`increase-qty-${item.merch_id}`}
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Remove Button */}
                  <button
                    onClick={() => removeFromCart(item.merch_id, item.size)}
                    className="text-[#666] hover:text-[#ff6b6b] transition-colors self-start"
                    disabled={loading}
                    data-testid={`remove-item-${item.merch_id}`}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
              
              {/* Clear Cart */}
              <button
                onClick={clearCart}
                className="w-full py-2 text-[#666] hover:text-[#ff6b6b] font-mono text-sm transition-colors"
                disabled={loading}
                data-testid="clear-cart-btn"
              >
                Clear Cart
              </button>
            </>
          )}
        </div>
        
        {/* Shipping Address (Optional) */}
        {cart.items.length > 0 && showShipping && (
          <div className="p-4 border-t border-[#1a1a1a] bg-[#0a0a0a]">
            <h3 className="text-white font-['Chakra_Petch'] uppercase text-sm mb-3 flex items-center gap-2">
              <Truck size={16} className="text-[#00CCFF]" />
              Shipping Address
            </h3>
            <div className="grid gap-3">
              <Input
                placeholder="Full Name"
                value={shippingAddress.name}
                onChange={(e) => setShippingAddress({ ...shippingAddress, name: e.target.value })}
                className="bg-[#111] border-[#1a1a1a] text-white font-mono text-sm"
              />
              <Input
                placeholder="Street Address"
                value={shippingAddress.street}
                onChange={(e) => setShippingAddress({ ...shippingAddress, street: e.target.value })}
                className="bg-[#111] border-[#1a1a1a] text-white font-mono text-sm"
              />
              <div className="grid grid-cols-2 gap-3">
                <Input
                  placeholder="City"
                  value={shippingAddress.city}
                  onChange={(e) => setShippingAddress({ ...shippingAddress, city: e.target.value })}
                  className="bg-[#111] border-[#1a1a1a] text-white font-mono text-sm"
                />
                <Input
                  placeholder="State/Province"
                  value={shippingAddress.state}
                  onChange={(e) => setShippingAddress({ ...shippingAddress, state: e.target.value })}
                  className="bg-[#111] border-[#1a1a1a] text-white font-mono text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  placeholder="ZIP/Postal Code"
                  value={shippingAddress.zip}
                  onChange={(e) => setShippingAddress({ ...shippingAddress, zip: e.target.value })}
                  className="bg-[#111] border-[#1a1a1a] text-white font-mono text-sm"
                />
                <Input
                  placeholder="Country"
                  value={shippingAddress.country}
                  onChange={(e) => setShippingAddress({ ...shippingAddress, country: e.target.value })}
                  className="bg-[#111] border-[#1a1a1a] text-white font-mono text-sm"
                />
              </div>
            </div>
          </div>
        )}
        
        {/* Footer - Summary & Checkout */}
        {cart.items.length > 0 && (
          <div className="p-4 border-t border-[#1a1a1a] bg-[#111] space-y-4">
            {/* Shipping Toggle */}
            <button
              onClick={() => setShowShipping(!showShipping)}
              className="w-full flex items-center justify-between py-2 text-[#888] hover:text-white font-mono text-sm transition-colors"
            >
              <span className="flex items-center gap-2">
                <Truck size={16} />
                {showShipping ? 'Hide Shipping' : 'Add Shipping Address'}
              </span>
              <span>{showShipping ? '-' : '+'}</span>
            </button>
            
            {/* Price Summary */}
            <div className="space-y-2 py-2 border-y border-[#1a1a1a]">
              <div className="flex justify-between text-[#888] font-mono text-sm">
                <span>Subtotal ({cart.item_count} items)</span>
                <span>${cart.total.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-[#666] font-mono text-xs">
                <span>Platform Fee (10%)</span>
                <span>${platformFee.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-white font-['Chakra_Petch'] text-lg">
                <span>Total</span>
                <span className="text-[#00ff88]">${grandTotal.toFixed(2)}</span>
              </div>
            </div>
            
            {/* Artist Revenue Note */}
            <p className="text-[#666] font-mono text-xs text-center">
              Artists receive 80% of each merch sale
            </p>
            
            {/* Checkout Button - redirects to full /cart page */}
            <Button
              onClick={() => { setIsOpen(false); window.location.href = '/cart'; }}
              disabled={loading || cart.items.length === 0}
              className="w-full bg-[#FFDA33] hover:bg-[#FFE866] text-black font-['Chakra_Petch'] uppercase py-6"
              data-testid="go-to-cart-btn"
            >
              <CreditCard size={20} className="mr-2" />
              Go to cart & checkout
            </Button>
            
            {/* Security Note */}
            <p className="text-[#666] font-mono text-xs text-center flex items-center justify-center gap-1">
              <Lock size={12} className="text-green-500" />
              Secure checkout powered by Stripe
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
